import { useState, useEffect, useCallback, useRef } from 'react';
import { SCOPES, DISCOVERY_DOC } from '../utils/api';

/**
 * Custom hook for Google Sheets API integration
 * Extracted from index.html monolith
 */
export const useGoogleSheets = (apiKey, clientId) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);
  const [tokenClient, setTokenClient] = useState(null);
  const [error, setError] = useState(null);
  // GIS's token-client callbacks are fixed at initTokenClient() time, so a
  // programmatic refreshToken() settles through this ref rather than a
  // per-call callback. Holds { promise, resolve, reject } for the single
  // in-flight refresh, or null. One shared promise means concurrent callers
  // (e.g. the two parallel reads in updateSheets both 401ing) all await the
  // same refresh instead of clobbering each other's resolvers.
  const refreshRef = useRef(null);

  // Settle the in-flight refresh exactly once. err => reject, else resolve.
  const settlePendingRefresh = useCallback((err) => {
    const pending = refreshRef.current;
    if (!pending) return;
    refreshRef.current = null;
    if (err) pending.reject(err);
    else pending.resolve();
  }, []);

  // Initialize GAPI
  useEffect(() => {
    if (!apiKey) return;

    const initGapi = () => {
      window.gapi.load('client', {
        callback: async () => {
          try {
            await window.gapi.client.init({
              apiKey: apiKey,
              discoveryDocs: [DISCOVERY_DOC],
            });
            setGapiLoaded(true);
          } catch (err) {
            console.error('GAPI init error details:', err);
            let errorMsg = err.message || (err.result?.error?.message) || JSON.stringify(err);
            setError(`GAPI client init error: ${errorMsg}`);
          }
        },
        onerror: (err) => {
          setError(`GAPI load error: ${err}`);
        }
      });
    };

    if (window.gapi) {
      initGapi();
    } else {
      // Wait for GAPI to load
      const checkGapi = setInterval(() => {
        if (window.gapi) {
          clearInterval(checkGapi);
          initGapi();
        }
      }, 100);
      return () => clearInterval(checkGapi);
    }
  }, [apiKey]);

  // Initialize GIS token client
  useEffect(() => {
    if (!clientId) return;

    const initGis = () => {
      try {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: SCOPES,
          callback: (response) => {
            if (response.error) {
              setError(`Auth error: ${response.error}`);
              settlePendingRefresh(new Error(response.error));
              return;
            }
            window.gapi.client.setToken(response);
            setIsSignedIn(true);
            settlePendingRefresh(null);
          },
          // GIS routes non-OAuth failures (popup blocked/closed, network) here,
          // NOT to `callback`. Without this a blocked silent-refresh popup would
          // never settle refreshToken()'s promise and the awaiting save would
          // hang forever.
          error_callback: (err) => {
            const msg = err?.type || err?.message || 'token request failed';
            setError(`Auth error: ${msg}`);
            settlePendingRefresh(new Error(msg));
          },
        });
        setTokenClient(client);
        setGisLoaded(true);
      } catch (err) {
        setError(`GIS init error: ${err.message}`);
      }
    };

    if (window.google?.accounts?.oauth2) {
      initGis();
    } else {
      const checkGis = setInterval(() => {
        if (window.google?.accounts?.oauth2) {
          clearInterval(checkGis);
          initGis();
        }
      }, 100);
      return () => clearInterval(checkGis);
    }
  }, [clientId, settlePendingRefresh]);

  const signIn = useCallback(() => {
    if (tokenClient) {
      tokenClient.requestAccessToken();
    }
  }, [tokenClient]);

  /**
   * refreshToken – attempts a silent (no-popup) token renewal when the
   * access token has expired mid-session (GIS tokens last ~1 hour). Resolves
   * once a fresh token is set, or rejects if silent renewal isn't possible
   * (e.g. third-party cookies blocked) — callers should fall back to
   * prompting the user to sign in again via signIn().
   */
  const refreshToken = useCallback(() => {
    if (!tokenClient) return Promise.reject(new Error('Token client not ready'));
    // Dedup: if a refresh is already in flight, everyone awaits the same one.
    if (refreshRef.current) return refreshRef.current.promise;

    let resolve, reject;
    const promise = new Promise((res, rej) => { resolve = res; reject = rej; });
    refreshRef.current = { promise, resolve, reject };
    try {
      tokenClient.requestAccessToken({ prompt: '' });
    } catch (err) {
      // Synchronous throw (rare) — settle so callers don't hang.
      settlePendingRefresh(err instanceof Error ? err : new Error(String(err)));
    }
    return promise;
  }, [tokenClient, settlePendingRefresh]);

  const signOut = useCallback(() => {
    const token = window.gapi.client.getToken();
    if (token) {
      window.google.accounts.oauth2.revoke(token.access_token);
      window.gapi.client.setToken(null);
    }
    setIsSignedIn(false);
  }, []);

  return {
    isSignedIn,
    gapiLoaded,
    gisLoaded,
    error,
    signIn,
    signOut,
    refreshToken,
  };
};
